import React, { useState } from "react";
import { Close as CloseIcon, Save as SaveIcon } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  Fab,
} from "@mui/material";
import LinkButton from "../LinkButton";
import createKeyword from "@dyl/apiClients/backendApi/createKeyword";
import updateKeyword from "@dyl/apiClients/backendApi/updateKeyword";
import { useSnackbar } from "notistack";
import AbortDialog from "./AbortDialog";
import ConfirmationDialog from './ConfirmationDialog';
import { useAuth } from "@dyl/systems/UserManager";

export default function KeywordModel({
  onClose,
  open,
  keyword = "",
  misspellings = "",
  title,
  mode = "NEW_ADD",
  id = null,
  refreshTable,
}) {
  const [inputKeyword, setInputKeyword] = useState(keyword);
  const [inputMisspellings, setInputMisspellings] = useState(misspellings);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [abortOpen, setAbortOpen] = useState(false);
  const [duplicateMisspellingsWord, setDuplicateMisspellingsWord] = useState([]);
  const [isDuplicateKeyword, setDuplicateKeyword] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();

  const handleSaveClick = () => {
    if (!inputKeyword.trim()) {
      enqueueSnackbar("Keyword should not be blank", { variant: "error" });
      return;
    }
    if (!inputMisspellings.trim()) {
      enqueueSnackbar("There should be at least one misspelling word", {
        variant: "error",
      });
      return;
    }
    setConfirmDialogOpen(true);
  };

  const onSave = async () => {
    try {
      const keywordLower = inputKeyword.trim().toLowerCase();

      const allowedMisspellings = Array.from(
        new Set(
          inputMisspellings
            .split(",")
            .map((word) => word.trim().toLowerCase())
            .filter((misspelling) => misspelling && misspelling !== keywordLower)
        )
      );

      let username = user?.attributes?.email;
      const body = { text: keywordLower, misspellings: allowedMisspellings, username };

      if (mode === "NEW_ADD") {
        await createKeyword(body);
      } else {
        await updateKeyword(id, body);
      }

      enqueueSnackbar(`Misspelling keyword ${id ? "updated" : "added"} successfully`, { variant: "success" });
      setInputKeyword("");
      setInputMisspellings("");
      onClose();
      refreshTable();
    } catch (err) {
      const { code, metadata } = err?.response?.data || {};

      if (code === 'ERR_283') {
        const { isDuplicateKeyword, duplicateMisspellings } = metadata || {};

        if (isDuplicateKeyword) setDuplicateKeyword(true);
        if (duplicateMisspellings) setDuplicateMisspellingsWord(duplicateMisspellings);

        setAbortOpen(true);
      } else {
        enqueueSnackbar(`Error while ${id ? "updating" : "adding"} misspelling keyword`, { variant: "error" });
      }
    } finally {
      setConfirmDialogOpen(false);
    }
  };

  const handleAbortClose = () => {
    setAbortOpen(false);
    setDuplicateKeyword(false);
    onClose();
  };

  return (
    <>
      <Dialog fullWidth maxWidth="md" onClose={onClose} open={open} BackdropProps={{ sx: { backgroundColor: "rgba(128, 128, 128, 1)" }}}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Grid item xs={3} marginBottom={"10px"}>
            <TextField
              id="keyword"
              label="Keyword"
              variant="filled"
              sx={{ maxWidth: "300px" }}
              onChange={(e) => setInputKeyword(e.target.value)}
              value={inputKeyword}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              id="misspellings"
              label="Acceptable misspellings"
              variant="filled"
              fullWidth
              onChange={(e) => setInputMisspellings(e.target.value)}
              value={inputMisspellings}
            />
            <Typography>
              Add misspellings here, separated by commas with no space between them. E.g., Word-1, Word-2, Word-3
            </Typography>
            <Typography>
              Hint: You can copy-paste words directly here.
            </Typography>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid marginTop={"15%"} container spacing={2} justifyContent="flex-end">
            <Grid item>
              <LinkButton
                component={Fab}
                variant="extended"
                onClick={onClose}
                size="medium"
              >
                <CloseIcon fontSize="small" /> &nbsp;Cancel
              </LinkButton>
            </Grid>
            <Grid item>
              <Fab
                color="primary"
                size="medium"
                variant="extended"
                onClick={handleSaveClick}
              >
                <SaveIcon /> &nbsp;Save
              </Fab>
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <ConfirmationDialog
        confirmDialogOpen={confirmDialogOpen}
        setConfirmDialogOpen={setConfirmDialogOpen}
        inputKeyword={inputKeyword}
        inputMisspellings={inputMisspellings}
        onSave={onSave}
      />

      <AbortDialog
        abortOpen={abortOpen}
        setAbortOpen={setAbortOpen}
        isDuplicateKeyword={isDuplicateKeyword}
        inputKeyword={inputKeyword}
        duplicateMisspellingsWord={duplicateMisspellingsWord}
        handleAbortClose={handleAbortClose}
      />
    </>
  );
}
